// One back pattern on every non-tab screen (docs/design/SIVUPALSTA.md "Shell"): "← <parent>" from
// the nav tree. The parent of a section's sub-route (/dictionary/w/…, /lessons/…, /letters/new) is
// the section, back where you left its root (the search, the list); on the phone a section listed
// under Lisää has Lisää as its parent (NavItem.parent overrides both). A page whose parent is not
// its section's root (a lesson's practice → the lesson) says so with useBackParent().
//
// Phone: the bar takes the top bar's title place. PC: a quiet line above the page, only on
// sub-routes (the rail is always there, so a section root needs none).
import { createContext, useContext, useEffect, useState, type Dispatch, type ReactNode, type SetStateAction } from 'react';
import { Link, useLocation } from 'react-router';
import { Icon } from '../components/Icon';
import { useT } from '../i18n';
import { INSTALL_PATH, MORE_PATH, type NavItem } from '../shell/nav';
import { withoutMarginParams } from './context';

export interface BackTarget {
  to: string;
  label: string;
}

const Override = createContext<Dispatch<SetStateAction<BackTarget | null>> | null>(null);
const Current = createContext<BackTarget | null>(null);

/** Holds a page's own back target (useBackParent); the shell wraps the app in it. */
export function BackProvider({ children }: { children: ReactNode }) {
  const [override, setOverride] = useState<BackTarget | null>(null);
  return (
    <Override.Provider value={setOverride}>
      <Current.Provider value={override}>{children}</Current.Provider>
    </Override.Provider>
  );
}

/** A page's own parent ("← Oppitunti 3"), while it is shown. Pass a stable target or null. */
export function useBackParent(target: BackTarget | null): void {
  const set = useContext(Override);
  const to = target?.to;
  const label = target?.label;
  useEffect(() => {
    if (!set) return;
    set(to && label ? { to, label } : null);
    return () => set(null);
  }, [set, to, label]);
}

/** Where a section root was left (path + search), so "← Sanakirja" returns to the search. */
const lastRoot = new Map<string, string>();

/** The back target of a location, from the nav tree. `phone`: Lisää counts as a parent. */
export function backTargetOf(
  pathname: string,
  search: string,
  section: NavItem | undefined,
  nav: readonly NavItem[],
  phone: boolean,
  labels: { nav: (id: NavItem['id']) => string; more: string },
): BackTarget | null {
  if (!section) return phone && pathname === INSTALL_PATH ? { to: MORE_PATH, label: labels.more } : null;
  const atRoot = pathname === section.path;
  if (atRoot) {
    lastRoot.set(section.id, pathname + withoutMarginParams(search));
    const parent = section.parent ? nav.find((n) => n.id === section.parent) : undefined;
    if (parent) return { to: lastRoot.get(parent.id) ?? parent.path, label: labels.nav(parent.id) };
    return phone && section.phone === 'more' ? { to: MORE_PATH, label: labels.more } : null;
  }
  return { to: lastRoot.get(section.id) ?? section.path, label: labels.nav(section.id) };
}

/** The bar. `where`: the phone top bar (always when there is a parent) or above the PC page (sub-routes). */
export function BackBar({ section, nav, where }: { section: NavItem | undefined; nav: readonly NavItem[]; where: 'topbar' | 'page' }) {
  const t = useT();
  const { pathname, search } = useLocation();
  const override = useContext(Current);
  const derived = backTargetOf(pathname, search, section, nav, where === 'topbar', { nav: (id) => t.nav[id], more: t.nav.more });
  // PC: the rail shows the section; a back line only where the page is below a section root.
  const target = override ?? (where === 'page' && section && pathname === section.path ? null : derived);
  if (!target) return null;
  return (
    <Link className={`backbar is-${where}`} to={target.to} aria-label={t.margin.back.label(target.label)}>
      <Icon name="back" size={18} />
      <span>{target.label}</span>
    </Link>
  );
}
